import { api } from '../../api.js';
import { els } from './dom.js';
import { truncate } from './format.js';
import { imageLimit } from './controls.js';
import { renderPromptSource, renderReferenceImages } from './result-view.js';

function toReferenceImage(asset) {
  return {
    assetId: asset.id,
    name: asset.model || `图片 #${asset.id}`,
    dataUrl: `/api/image/assets/${encodeURIComponent(asset.id)}`,
    source: 'listing_asset',
  };
}

function selectedAssetIds(state) {
  return new Set(
    state.referenceImages
      .map((item) => item.assetId)
      .filter(Boolean)
      .map(String),
  );
}

function parseMetadata(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function setSelectedAssets(state, assets) {
  state.referenceSource = assets.length ? 'listing_assets' : '';
  state.referenceImages = assets.map(toReferenceImage).slice(0, imageLimit(state));
  renderReferenceImages(state);
  renderPromptSource(state);
}

function createElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined && text !== null) element.textContent = text;
  return element;
}

function imageSuiteLink(listingId, text) {
  const link = createElement('a', 'video-mini-link', text);
  link.href = `/image-suite.html?fromListing=${encodeURIComponent(listingId)}`;
  return link;
}

function createSourceHint(listingId, text, linkText) {
  const hint = createElement('div', 'video-source-hint');
  hint.append(document.createTextNode(text));
  if (listingId && linkText) {
    hint.append(document.createTextNode(' '), imageSuiteLink(listingId, linkText));
  }
  return hint;
}

function imageAssetUrl(assetId) {
  return `/api/image/assets/${encodeURIComponent(assetId)}`;
}

function createImageAssetCard(asset, active, onSelect) {
  const metadata = parseMetadata(asset.metadata);
  const label = metadata.slotNumber ? `Slot ${metadata.slotNumber}` : `图片 #${asset.id}`;
  const subLabel = [metadata.slotType, asset.model].filter(Boolean).join(' · ') || label;
  const source = imageAssetUrl(asset.id);
  const card = createElement('button', 'video-asset-card');
  card.type = 'button';
  card.classList.toggle('active', active);
  card.dataset.assetId = String(asset.id);

  const image = document.createElement('img');
  image.src = source;
  image.alt = label;
  image.loading = 'lazy';

  const caption = createElement('span', '', truncate(subLabel, 32));
  caption.title = subLabel;
  const preview = createElement('span', 'hover-preview');
  const previewImage = document.createElement('img');
  previewImage.src = source;
  previewImage.alt = '';
  preview.appendChild(previewImage);

  card.append(image, caption, preview);
  card.addEventListener('click', onSelect);
  return card;
}

export function parseIntentImageAssetIds(search = window.location.search) {
  const params = new URLSearchParams(search);
  const raw = params.get('imageAssetIds') || params.get('assetIds') || '';
  return [...new Set(raw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean))];
}

export function renderListingImagePicker(state, { refreshSubmitState, markPreviewStale } = {}) {
  const host = els.listingImagePicker();
  if (!host) return;
  if (!state.listing?.id) {
    host.replaceChildren();
    return;
  }
  const images = (state.listingImages || []).filter((item) => item.status === 'completed' && item.id);
  if (!images.length) {
    host.replaceChildren(createSourceHint(state.listing.id, '当前 Listing 还没有可用套图。', '去套图生成'));
    return;
  }

  const limit = imageLimit(state);
  const chosen = selectedAssetIds(state);
  const toolbar = createElement('div', 'video-asset-toolbar');
  toolbar.append(
    createElement('span', '', `从当前 Listing 套图选择（最多 ${limit} 张）`),
    imageSuiteLink(state.listing.id, '管理套图')
  );
  const grid = createElement('div', 'video-asset-grid');
  images.forEach((asset) => {
    grid.appendChild(createImageAssetCard(asset, chosen.has(String(asset.id)), () => {
      const id = String(asset.id);
      const current = selectedAssetIds(state);
      const nextIds = new Set(current);
      if (limit === 1) {
        nextIds.clear();
        nextIds.add(id);
      } else if (nextIds.has(id)) {
        nextIds.delete(id);
      } else if (nextIds.size < limit) {
        nextIds.add(id);
      }
      const nextAssets = images.filter((asset) => nextIds.has(String(asset.id)));
      setSelectedAssets(state, nextAssets);
      renderListingImagePicker(state, { refreshSubmitState, markPreviewStale });
      refreshSubmitState?.();
      markPreviewStale?.('参考图已切换为当前 Listing 套图，请重新生成视频。');
    }));
  });
  host.replaceChildren(toolbar, grid);
}

export async function loadListingImagePicker(state, callbacks = {}) {
  if (!state.listing?.id) {
    state.listingImages = [];
    renderListingImagePicker(state, callbacks);
    return;
  }
  const host = els.listingImagePicker();
  if (host) host.replaceChildren(createSourceHint(null, '正在加载当前 Listing 套图...'));
  try {
    const resp = await api.getImageAssets({ listingId: state.listing.id, includePending: 0, page: 1, pageSize: 30 });
    state.listingImages = resp?.images || [];
    const intentAssetIds = parseIntentImageAssetIds();
    if (intentAssetIds.length && !state.intentImageAssetsApplied) {
      const intentSet = new Set(intentAssetIds.map(String));
      const matchedAssets = state.listingImages.filter((asset) => intentSet.has(String(asset.id)));
      if (matchedAssets.length) setSelectedAssets(state, matchedAssets);
      if (matchedAssets.length) callbacks.refreshSubmitState?.();
      state.intentImageAssetsApplied = true;
    }
  } catch {
    state.listingImages = [];
  }
  renderListingImagePicker(state, callbacks);
}
